import React, { useState, useEffect, useCallback } from "react";
import { Badge, Progress, Tag, Tooltip, Button, Select, Input } from "antd";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ClockCircleOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { request } from "../../helper/axios_helper";
import "../../styles/job-queue-panel.css";

const { Option } = Select;

const TIME_OPTIONS = [
  { label: "6 hrs", value: 6 },
  { label: "12 hrs", value: 12 },
  { label: "24 hrs", value: 24 },
  { label: "48 hrs", value: 48 },
];

// Parse "dd-MM-yyyy HH:mm:ss" → ms timestamp (0 if invalid)
const parseRunStart = (runStart) => {
  if (!runStart) return 0;
  try {
    const [datePart, timePart] = runStart.split(" ");
    const [dd, mm, yyyy] = datePart.split("-");
    return new Date(`${yyyy}-${mm}-${dd}T${timePart}`).getTime();
  } catch {
    return 0;
  }
};

function JobQueuePanel() {
  const [collapsed, setCollapsed] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [runningCount, setRunningCount] = useState(0);

  // ─── Filters ──────────────────────────────────────────────
  const [hours, setHours] = useState(24);
  const [runByFilter, setRunByFilter] = useState("");
  const [runByOptions, setRunByOptions] = useState([]);

  const fetchJobs = useCallback(() => {
    const params = new URLSearchParams({ hours });
    if (runByFilter) params.append("runBy", runByFilter);

    request("GET", `/api/auth/job/queue/active?${params.toString()}`)
      .then((res) => {
        const data = (res.data || []).sort(
          (a, b) => parseRunStart(b.run_start) - parseRunStart(a.run_start),
        );
        setJobs(data);

        // Build unique run-by options from results
        const users = [
          ...new Set(
            data.map((j) => j.created_user || j.run_by).filter(Boolean),
          ),
        ];
        setRunByOptions((prev) => {
          const merged = [...new Set([...prev, ...users])];
          return merged;
        });

        const running = data.filter(
          (j) => j.job_status === "In Progress" || j.job_status === "Running",
        ).length;
        setRunningCount(running);
      })
      .catch(() => {});
  }, [hours, runByFilter]);

  // Poll every 3s
  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 3000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "In Progress":
      case "Running":
        return <SyncOutlined spin style={{ color: "#1890ff" }} />;
      case "Complete":
        return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
      case "Failed":
      case "Error":
        return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
      default:
        return <ClockCircleOutlined style={{ color: "#bbb" }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "In Progress":
      case "Running":
        return "processing";
      case "Complete":
        return "success";
      case "Failed":
      case "Error":
        return "error";
      default:
        return "default";
    }
  };

  const getProgressColor = (status) => {
    switch (status) {
      case "Complete":
        return "#52c41a";
      case "Failed":
      case "Error":
        return "#ff4d4f";
      default:
        return "#1890ff";
    }
  };

  const formatElapsed = (runStart) => {
    if (!runStart) return "";
    try {
      const elapsed = Math.floor((Date.now() - parseRunStart(runStart)) / 1000);
      const m = Math.floor(elapsed / 60).toString().padStart(2, "0");
      const s = (elapsed % 60).toString().padStart(2, "0");
      return `${m}:${s}`;
    } catch {
      return "";
    }
  };

  const getCategoryLabel = (job) => {
    const cat =
      job.task_category || job.task_sub_category || job.task_type || "Job";
    const name = job.name || job.task_sub_category || "";
    return name ? `${cat} · ${name}` : cat;
  };

  return (
    <div className={`job-queue-panel ${collapsed ? "collapsed" : "expanded"}`}>
      {/* ─── Toggle button ──────────────────────────────────── */}
      <div
        className="job-queue-toggle"
        onClick={() => setCollapsed(!collapsed)}
      >
        <Badge count={runningCount} size="small" offset={[-2, 2]}>
          {collapsed ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
        </Badge>
        {collapsed && <span className="job-queue-toggle-label">Jobs</span>}
      </div>

      {/* ─── Panel content ──────────────────────────────────── */}
      {!collapsed && (
        <div className="job-queue-content">
          {/* Header */}
          <div className="job-queue-header">
            <span className="job-queue-title">Job Queue</span>
            <span className="job-queue-subtitle">
              {runningCount > 0 ? `${runningCount} running` : "No active jobs"}
            </span>
          </div>

          {/* ─── Filters ────────────────────────────────────── */}
          <div className="job-queue-filters">
            {/* Time filter */}
            <div className="job-queue-filter-row">
              <FilterOutlined style={{ color: "#888", fontSize: 12 }} />
              <Select
                value={hours}
                onChange={setHours}
                size="small"
                style={{ flex: 1 }}
                options={TIME_OPTIONS}
              />
            </div>

            {/* Run by filter */}
            <div className="job-queue-filter-row">
              <span className="job-queue-filter-label">By</span>
              <Select
                value={runByFilter || undefined}
                onChange={(val) => setRunByFilter(val || "")}
                placeholder="All users"
                allowClear
                size="small"
                style={{ flex: 1 }}
              >
                {runByOptions.map((u) => (
                  <Option key={u} value={u}>
                    {u}
                  </Option>
                ))}
              </Select>
            </div>
          </div>

          {/* ─── Job list ────────────────────────────────────── */}
          <div className="job-queue-list">
            {jobs.length === 0 ? (
              <div className="job-queue-empty">
                No jobs in last {hours}h
                {runByFilter ? ` for ${runByFilter}` : ""}
              </div>
            ) : (
              jobs.map((job) => (
                <div key={job.job_id} className="job-queue-item">
                  <div className="job-queue-item-header">
                    <div className="job-queue-item-name">
                      {getStatusIcon(job.job_status)}
                      <Tooltip title={job.job_id}>
                        <span className="job-queue-item-label">
                          {getCategoryLabel(job)}
                        </span>
                      </Tooltip>
                    </div>
                    <Tag
                      color={getStatusColor(job.job_status)}
                      style={{ margin: 0 }}
                    >
                      {job.job_status}
                    </Tag>
                  </div>

                  <Progress
                    percent={job.progress_pct || 0}
                    size="small"
                    strokeColor={getProgressColor(job.job_status)}
                    showInfo={
                      job.job_status === "Complete" ||
                      job.job_status === "Failed"
                    }
                    status={
                      job.job_status === "Failed" || job.job_status === "Error"
                        ? "exception"
                        : job.job_status === "Complete"
                          ? "success"
                          : "active"
                    }
                    style={{ marginBottom: 4 }}
                  />

                  <div className="job-queue-item-footer">
                    {/* Run by */}
                    {(job.created_user || job.run_by) && (
                      <span className="job-queue-runby">
                        👤 {job.created_user || job.run_by}
                      </span>
                    )}
                    {/* Elapsed time */}
                    {(job.job_status === "In Progress" ||
                      job.job_status === "Running") && (
                      <span className="job-queue-elapsed">
                        ⏱ {formatElapsed(job.run_start)}
                      </span>
                    )}
                    {/* Error */}
                    {job.error_description && (
                      <Tooltip title={job.error_description}>
                        <span className="job-queue-error">
                          Error — hover for detail
                        </span>
                      </Tooltip>
                    )}
                    <span className="job-queue-id">{job.job_id}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default JobQueuePanel;
